import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@tonic/shared/components/ui';
import { Calendar, Wrench, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { mockRentals, mockMaintenanceTasks } from './mock-data';

// Extract all items that need maintenance from active rentals
const getItemsNeedingMaintenance = () => {
  const items = [];
  mockRentals.forEach(rental => {
    if (rental.status === 'active') {
      rental.items.forEach(item => {
        if (item.condition.current === 'fair' || item.condition.current === 'poor') {
          items.push({
            ...item,
            rentalId: rental.id,
            customer: rental.customer,
            location: rental.location.pickup
          });
        }
      });
    }
  });
  return items;
};

// Get maintenance history from all rentals
const getMaintenanceHistory = () => {
  const history = [];
  mockRentals.forEach(rental => {
    if (rental.maintenanceHistory) {
      rental.maintenanceHistory.forEach(record => {
        history.push({
          ...record,
          rentalId: rental.id,
          itemName: rental.items.find(item => item.productId === record.itemId)?.name
        });
      });
    }
  });
  return history.sort((a, b) => new Date(b.date) - new Date(a.date));
};

const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'completed':
      return 'default';
    case 'in_progress':
      return 'secondary';
    case 'scheduled':
      return 'outline';
    default:
      return 'outline';
  }
};

const getPriorityBadgeVariant = (priority) => {
  switch (priority) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'outline';
    default:
      return 'outline';
  }
};

export function RentalMaintenancePage() {
  const [activeTab, setActiveTab] = React.useState('tasks');
  const itemsNeedingMaintenance = getItemsNeedingMaintenance();
  const maintenanceHistory = getMaintenanceHistory();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Maintenance Management</h1>
        <p className="text-muted-foreground">
          Track and manage maintenance tasks for rental items.
        </p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Maintenance Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Scheduled</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {mockMaintenanceTasks.filter(task => task.status === 'scheduled').length}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {mockMaintenanceTasks.filter(task => task.status === 'in_progress').length}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Needs Attention</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{itemsNeedingMaintenance.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completed Today</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {maintenanceHistory.filter(record =>
                      new Date(record.date).toDateString() === new Date().toDateString()
                    ).length}
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="tasks" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tasks">Active Tasks</TabsTrigger>
            <TabsTrigger value="needs-maintenance">Needs Maintenance</TabsTrigger>
            <TabsTrigger value="history">Maintenance History</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks">
            <Card>
              <CardHeader>
                <CardTitle>Active Maintenance Tasks</CardTitle>
                <CardDescription>
                  View and manage ongoing and scheduled maintenance tasks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Scheduled</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockMaintenanceTasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{task.itemName}</div>
                            <div className="text-sm text-muted-foreground">{task.productId}</div>
                          </div>
                        </TableCell>
                        <TableCell>{task.type}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(task.status)}>
                            {task.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPriorityBadgeVariant(task.priority)}>
                            {task.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>{task.assignedTo}</TableCell>
                        <TableCell>{task.scheduledDate}</TableCell>
                        <TableCell>{task.location}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="needs-maintenance">
            <Card>
              <CardHeader>
                <CardTitle>Items Needing Maintenance</CardTitle>
                <CardDescription>
                  Items reported or identified as requiring maintenance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemsNeedingMaintenance.map((item) => (
                      <TableRow key={item.productId}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.name}</div>
                            <div className="text-sm text-muted-foreground">{item.sku}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.condition.current === 'poor' ? 'destructive' : 'secondary'}>
                            {item.condition.current}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{item.customer.name}</div>
                          <div className="text-sm text-muted-foreground">{item.customer.email}</div>
                        </TableCell>
                        <TableCell>{item.location}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            Schedule
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Maintenance History</CardTitle>
                <CardDescription>
                  View completed maintenance tasks and repairs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Technician</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenanceHistory.map((record, index) => (
                      <TableRow key={`${record.itemId}-${index}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{record.itemName}</div>
                            <div className="text-sm text-muted-foreground">{record.itemId}</div>
                          </div>
                        </TableCell>
                        <TableCell>{record.type}</TableCell>
                        <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                        <TableCell>{record.technician}</TableCell>
                        <TableCell>{record.notes}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
} 
